import type { ReactNode } from "react";
import { differenceInMinutes, parseISO } from "date-fns";
import { formatBusinessClock } from "@/lib/local-date";
import { formatBusinessDateTime } from "@/lib/business-date";
import { cn, getSentimentColors, getSentimentIconColor } from "@/lib/utils";
import { linkifyText } from "@/lib/linkify";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MessageSquareHeart, MessageSquareWarning } from "lucide-react";

export type TranscriptMessage = {
  role: "agent" | "customer";
  content: string;
  /** Set only on a structured (texts) message — see `messages` below. A
   * parsed call transcript never carries one, so the bubble's timestamp line
   * and the gap divider both stay absent on that path, unchanged. */
  at?: string | null;
  /** The source contact id, structured-messages mode only — lets a caller's
   * `renderMessageActions` (e.g. a per-message "⋯ move this message alone")
   * look the original record back up. Never read by this component itself. */
  id?: string;
};

/** Bubbles are split onto their own day/time divider once the gap since the
 * previous message passes this. Texts in one conversation can span days; a
 * silent two-hour jump mid-exchange reads like the same back-and-forth. */
const GAP_DIVIDER_MINUTES = 120;

export type TranscriptMoment = {
  moment_index?: number;
  moment_type: string;
  sentiment?: string;
  quote?: string;
  description: string;
};

const SPEAKER_SPLIT_RE = /(?=\b(?:Agent|Customer):)/i;

export function parseTranscript(transcript: string): TranscriptMessage[] {
  return transcript
    .split(SPEAKER_SPLIT_RE)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => {
      const lower = segment.toLowerCase();
      const role: TranscriptMessage["role"] = lower.startsWith("agent:")
        ? "agent"
        : "customer";

      let content: string;
      if (lower.startsWith("agent:")) {
        content = segment.substring("agent:".length).trim();
      } else if (lower.startsWith("customer:")) {
        content = segment.substring("customer:".length).trim();
      } else {
        content = segment;
      }

      return { role, content };
    })
    .filter((msg) => msg.content.length > 0);
}

type TranscriptViewProps = {
  transcript: string | null;
  moments?: TranscriptMoment[];
  className?: string;
  /** Optional per-message actions (e.g. feedback thumbs). When omitted, the
   * bubbles render with no action affordances — this is what lets the view be
   * dropped into surfaces that have no feedback context / call id. */
  renderMessageActions?: (message: TranscriptMessage) => ReactNode;
  /** Optional content under a bubble's words (e.g. a text's photos). Structured-messages mode only. */
  renderMessageFooter?: (message: TranscriptMessage) => ReactNode;
  /** A second input mode: pre-built messages (a text conversation's own
   * back-and-forth) instead of a single `Agent:`/`Customer:` string to parse.
   * When set, `transcript` is ignored entirely — the two modes are mutually
   * exclusive, never merged. Each message's `at` (required here) drives a
   * per-bubble timestamp and the long-gap divider between messages. */
  messages?: TranscriptMessage[];
};

/** Presentational transcript renderer: Agent/Customer chat bubbles with inline
 * sentiment-highlighted moment quotes. Pure props — no data fetching, no
 * feedback context, no call id. Shared by the Conversations call sheet
 * (`TranscriptMessages`, which adds feedback), the LCC contact-history panel,
 * and (via the `messages` prop) the deal timeline's text conversation card. */
export function TranscriptView({
  transcript,
  moments = [],
  className,
  renderMessageActions,
  renderMessageFooter,
  messages: structuredMessages,
}: TranscriptViewProps) {
  // `structuredMessages` entirely replaces the parse — never merged with it,
  // so a caller that ever passed both would have to pick one on purpose. The
  // `transcript` path (every existing caller) is untouched: this only reads
  // `structuredMessages`, which is undefined for every one of them.
  const messages = structuredMessages ?? parseTranscript(transcript ?? "");

  const highlightMomentQuotes = (content: string) => {
    if (!moments?.length) return <>{linkifyText(content)}</>;

    const matchingMoments = moments.filter(
      (moment) => moment.quote && content.includes(moment.quote)
    );

    if (!matchingMoments.length) return <>{linkifyText(content)}</>;

    matchingMoments.sort((a, b) => {
      const posA = content.indexOf(a.quote!);
      const posB = content.indexOf(b.quote!);
      return posA - posB;
    });

    const parts: ReactNode[] = [];
    let lastIndex = 0;

    matchingMoments.forEach((moment) => {
      const quote = moment.quote!;
      const startIndex = content.indexOf(quote, lastIndex);

      if (startIndex > lastIndex) {
        parts.push(linkifyText(content.substring(lastIndex, startIndex)));
      }

      const sentimentColor = getSentimentColors(moment.sentiment);

      const SentimentIcon = moment.sentiment?.includes("positive")
        ? MessageSquareHeart
        : MessageSquareWarning;

      const iconColor = getSentimentIconColor(moment.sentiment);

      parts.push(
        <TooltipProvider key={startIndex}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="group relative">
                <span className="border-b-2 border-dashed border-primary cursor-help">
                  {quote}
                </span>
                <SentimentIcon
                  className={`inline-block ml-1 h-3.5 w-3.5 ${iconColor}`}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent
              sideOffset={5}
              className={cn(sentimentColor, "text-foreground p-3")}
            >
              <div className="max-w-xs flex flex-col gap-2">
                <p className="font-bold">{moment.moment_type}</p>
                <p className="text-xs">{moment.description}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );

      lastIndex = startIndex + quote.length;
    });

    if (lastIndex < content.length) {
      parts.push(linkifyText(content.substring(lastIndex)));
    }

    return <>{parts}</>;
  };

  if (!transcript && !structuredMessages?.length) {
    return (
      <div className="flex flex-col gap-3 relative max-h-[500px] overflow-y-auto">
        <div className="flex gap-3 w-full justify-center">
          <div className="whitespace-pre-wrap text-sm">
            No transcript available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {moments?.filter((m) => m.quote).length > 0 && (
        <div className="mb-2 text-xs text-muted-foreground flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 border-b-2 border-dashed border-primary"></span>
            <span>Important moments</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageSquareHeart className="h-3.5 w-3.5 text-green-600" />
            <span>Positive</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageSquareWarning className="h-3.5 w-3.5 text-amber-600" />
            <span>Negative/Concern</span>
          </div>
        </div>
      )}
      <div
        className={cn(
          "flex flex-col gap-3 relative max-h-[500px] overflow-y-auto",
          className
        )}
      >
        {messages.map((message, index) => {
          // Only the `messages` (texts) path sets `at` on consecutive
          // entries, so this is a no-op on the `transcript` (calls) path —
          // both `message.at` and the previous one are undefined there.
          const previous = index > 0 ? messages[index - 1] : null;
          const showDivider =
            !!message.at &&
            !!previous?.at &&
            differenceInMinutes(parseISO(message.at), parseISO(previous.at)) >=
              GAP_DIVIDER_MINUTES;
          return (
            <div key={index} className="contents">
              {showDivider ? (
                <div className="flex items-center justify-center py-1 text-xs text-muted-foreground">
                  {formatBusinessDateTime(message.at!)}
                </div>
              ) : null}
              <div
                className={cn(
                  "flex gap-3 w-full",
                  message.role === "customer" ? "justify-start" : "justify-end",
                )}
              >
                <div
                  className={cn(
                    "rounded-lg px-4 py-2 max-w-[80%] text-sm",
                    message.role === "agent"
                      ? "bg-sage text-sage-foreground"
                      : "bg-muted",
                    "shadow-sm",
                  )}
                >
                  <div className="font-bold text-sm flex items-center gap-2">
                    {message.role === "agent" ? "Agent" : "Customer"}
                    {/* One `ml-auto` claims the row's free space; the timestamp and the per-message
                        actions share it rather than each trying to push the other further right. */}
                    {message.at || renderMessageActions ? (
                      <span className="ml-auto flex items-center gap-2">
                        {renderMessageActions ? renderMessageActions(message) : null}
                        {message.at ? (
                          <span className="text-xs font-normal opacity-70">{formatBusinessClock(message.at)}</span>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                  <div className="whitespace-pre-wrap break-words text-sm">
                    {/* Moment-quote highlighting and URL linkifying are both regex passes over the
                        same string; moments only ever ride the call-transcript path (`transcript`,
                        never `messages`), so the two never need to compose. */}
                    {moments?.length ? highlightMomentQuotes(message.content) : linkifyText(message.content)}
                  </div>
                  {structuredMessages && renderMessageFooter ? renderMessageFooter(message) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
